import { ApolloError, useQuery } from '@apollo/client'
import { Groups, Person, Add } from '@mui/icons-material'
import { Box, Button, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { Architecture, BucketType, BucketsByUserIdDocument } from '../../../../../graphql/generated'
import { drawerVar, modalVar } from '../../../../../providers/apollo/cache'
import logger from '../../../../../utils/logger'
import useAlerts from '../../../hooks/useAlerts'
import useUserProfileUpdate from '../../../hooks/useUserProfileUpdate'
import useUserSubscription from '../../../hooks/useUserSubcription'
import BucketMenuNew from './BucketMenuNew'

interface Props {
  userId: string
}

export default function BucketSwitcher({ userId }: Readonly<Props>) {
  const theme = useTheme()
  const navigate = useNavigate()
  const { createAlert } = useAlerts()
  const { bucketId } = useParams<{ bucketId: string }>()
  const { mutation } = useUserProfileUpdate()
  const { isSubscriptionActive } = useUserSubscription()

  const disabled = !isSubscriptionActive

  const results = useQuery(BucketsByUserIdDocument, {
    variables: { userId },
    onError: (e) => {
      logger.error(e)
      createAlert('We are having trouble retrieving your buckets. Please try again later.')
    }
  })

  const buckets = results.data?.bucketsByUserId?.items ?? []
  const bucketsWithoutLegacy = buckets.filter((bucket) => bucket.architecture !== Architecture.Kafka)

  const openModal = (modalName: 'createBucket' | 'bucketRename') => {
    drawerVar(null)
    modalVar(modalName)
  }

  const handleClick = (currentBucketId: string) => () => {
    drawerVar(null)
    mutation({
      variables: { data: { currentBucketId } },
      onCompleted: () => {
        // Todo remove this edge case once buckets are migrated
        if (currentBucketId === userId) {
          window.location.href = `/u/${userId}`
          return
        }

        navigate(`/bucket/${currentBucketId}`)
      },
      onError: (error: ApolloError) => {
        logger.error(error)

        if (currentBucketId === userId) {
          window.location.href = `/u/${userId}`
          return
        }

        navigate(`/bucket/${currentBucketId}`)
      }
    })
  }

  if (!results.called || results.loading) {
    return <Skeleton variant="rectangular" width="100%" height="50px" />
  }

  return (
    <Box>
      <Stack
        direction="row"
        gap="6px"
        alignItems="flex-start"
        flexWrap="wrap"
        sx={{ marginTop: '4px', maxHeight: '275px', overflowY: 'auto', borderBottom: `1px solid ${theme.palette.customGrey[200]}` }}
      >
        {bucketsWithoutLegacy.map((bucket) => {
          return (
            <Stack
              key={bucket.id}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                flexDirection: 'row',
                border: bucketId === bucket.id ? `1px solid ${theme.palette.customViolet[500]}` : `1px solid transparent`,
                width: '100%',
                borderRadius: '4px',
                bgcolor: bucketId === bucket.id ? theme.palette.customViolet[50] : 'transparent',
                '&:hover': {
                  bgcolor: theme.palette.customViolet[50],
                  border: `1px solid ${theme.palette.customViolet[500]}`
                }
              }}
            >
              <Button
                onClick={handleClick(bucket.id)}
                data-test={`bucket-switcher-${bucket.id}`}
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  flex: 1,
                  gap: '2px',
                  minWidth: 0,
                  padding: '4px 8px',
                  color: theme.palette.secondary.main,
                  '&:hover': {
                    bgcolor: 'transparent'
                  }
                }}
              >
                <Stack alignItems="flex-start">
                  <Typography
                    component="h3"
                    sx={{
                      fontWeight: bucketId === bucket.id ? 800 : 500,
                      fontSize: '1.125rem',
                      maxWidth: '200px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                  >
                    {bucket.title}
                  </Typography>
                  <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: bucketId === bucket.id ? theme.palette.secondary.main : theme.palette.customGrey[500],
                        lineHeight: 1
                      }}
                    >
                      {bucket.bucketType === BucketType.Group ? <Groups sx={{ fontSize: '1.25rem' }} /> : <Person sx={{ fontSize: '1.25rem' }} />}
                    </Box>
                    <Typography sx={{ fontSize: '.75rem', fontWeight: 500 }}>
                      {bucket.bucketType === BucketType.Group
                        ? `Shared with ${bucket.bucketUserCount} ${bucket.bucketUserCount === 1 ? 'Person' : 'People'}`
                        : 'Personal'}
                    </Typography>
                  </Stack>
                </Stack>
              </Button>
              {bucketId === bucket.id && (
                <Box sx={{ paddingRight: '8px' }}>
                  <BucketMenuNew />
                </Box>
              )}
            </Stack>
          )
        })}
      </Stack>
      <Box sx={{ paddingTop: '8px' }}>
        <Button
          disabled={disabled}
          variant="contained"
          color="primary"
          onClick={() => openModal('createBucket')}
          sx={{ display: 'flex', flexDirection: 'row', gap: '4px', width: '100%', borderRadius: '40px' }}
          startIcon={<Add />}
        >
          Create a Bucket
        </Button>
      </Box>
    </Box>
  )
}
